import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { captureException } from '@sentry/react'
import {
  EDIT_STORAGE_FLOW_STATE,
  PACKAGING_FLOW_STATE,
  RETURN_FLOW_STATE,
  SQUARE_METER_FLOW_STATE,
  STORAGE_FLOW_STATE,
  STORAGE_UNIT_FLOW_STATE,
  UI_STATE,
} from 'consts'
/* eslint-disable local-rules/no-use-dispatch*/
import { useDispatch } from 'react-redux'

import {
  editStorageFlow,
  item,
  packagingFlow,
  returnFlow,
  squareMeterFlow,
  storageFlow,
  storageUnitFlow,
  ui,
  user,
} from './ducks'
import { initialState } from './ducks/storage/storageFlow'
import { loggerMiddleware } from './middleware/logger'

const reducer = combineReducers({
  item,
  ui,
  user,
  storageFlow,
  editStorageFlow,
  returnFlow,
  packagingFlow,
  squareMeterFlow,
  storageUnitFlow,
})

const preloadedState: any = {}

const rehydrateState = (key: string) => {
  try {
    const persistedState = sessionStorage.getItem(key)
    if (persistedState) {
      return JSON.parse(persistedState)
    }
    return null
  } catch (error) {
    captureException(error)
  }
}

const storageFlowState = rehydrateState(STORAGE_FLOW_STATE)
const editStorageFlowState = rehydrateState(EDIT_STORAGE_FLOW_STATE)
const returnFlowState = rehydrateState(RETURN_FLOW_STATE)
const packagingFlowState = rehydrateState(PACKAGING_FLOW_STATE)
const squareMeterFlowState = rehydrateState(SQUARE_METER_FLOW_STATE)
const uiState = rehydrateState(UI_STATE)
const storageUnitFlowState = rehydrateState(STORAGE_UNIT_FLOW_STATE)

if (storageFlowState) {
  preloadedState.storageFlow = { ...initialState, ...storageFlowState }
}

if (editStorageFlowState) {
  preloadedState.editStorageFlow = editStorageFlowState
}

if (returnFlowState) {
  preloadedState.returnFlow = returnFlowState
}

if (packagingFlowState) {
  preloadedState.packagingFlow = packagingFlowState
}

if (squareMeterFlowState) {
  preloadedState.squareMeterFlow = squareMeterFlowState
}

if (uiState) {
  preloadedState.ui = uiState
}

if (storageUnitFlowState) {
  preloadedState.storageUnitFlow = storageUnitFlowState
}

const store = configureStore({
  reducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(loggerMiddleware),
  preloadedState,
})

store.subscribe(() => {
  sessionStorage.setItem(STORAGE_FLOW_STATE, JSON.stringify(store.getState().storageFlow))
  sessionStorage.setItem(EDIT_STORAGE_FLOW_STATE, JSON.stringify(store.getState().editStorageFlow))
  sessionStorage.setItem(RETURN_FLOW_STATE, JSON.stringify(store.getState().returnFlow))
  sessionStorage.setItem(PACKAGING_FLOW_STATE, JSON.stringify(store.getState().packagingFlow))
  sessionStorage.setItem(SQUARE_METER_FLOW_STATE, JSON.stringify(store.getState().squareMeterFlow))
  sessionStorage.setItem(UI_STATE, JSON.stringify(store.getState().ui))
  sessionStorage.setItem(STORAGE_UNIT_FLOW_STATE, JSON.stringify(store.getState().storageUnitFlow))
})
export { store }

export type RootState = ReturnType<typeof store.getState>

export type AppDispatch = typeof store.dispatch
export const useAppDispatch: () => AppDispatch = useDispatch // Export a hook that can be reused to resolve types
